import { useEffect, useRef, useState } from "react";
import { Navbar } from "../components/Navbar";
import { UploadDialog } from "../components/UploadDialog";
import { AISearchDialog } from "../components/AISearch";
import { api, API } from "../lib/api";
import { Heart, MessageCircle, Share2, Volume2, VolumeX, Plus, Film } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const Reel = ({ media, active, muted, onToggleMute, onLike }) => {
  const ref = useRef(null);
  const [likes, setLikes] = useState(media.likes || 0);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (active) { v.play().catch(() => {}); } else { v.pause(); v.currentTime = 0; }
  }, [active]);

  const like = async () => {
    if (liked) return;
    setLiked(true); setLikes((n) => n + 1);
    try { await api.post(`/media/${media.id}/like`); } catch {}
    onLike?.();
  };

  return (
    <div
      className="snap-start relative h-screen w-full flex items-center justify-center"
      data-testid={`reel-${media.id}`}
    >
      <div className="relative h-[88vh] w-[min(92vw,calc(88vh*9/16))] bg-black border border-white/10 overflow-hidden">
        <video
          ref={ref}
          src={`${API}/media/${media.id}/stream`}
          className="absolute inset-0 w-full h-full object-cover"
          loop
          playsInline
          muted={muted}
          controls={false}
          onClick={(e) => { const v = e.currentTarget; v.paused ? v.play() : v.pause(); }}
        />

        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-wider px-1.5 py-0.5 bg-[#E60000] text-white font-bold">REEL</span>
          <button onClick={onToggleMute} data-testid="reel-mute-toggle" className="text-white/80 hover:text-white">
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
          <div className="text-white">
            <div className="font-heading font-black text-xl tracking-tight">{media.title}</div>
            {media.description && <div className="text-white/70 text-sm mt-1 line-clamp-2">{media.description}</div>}
          </div>
        </div>

        {/* Side actions */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col items-center gap-5">
          <button onClick={like} data-testid={`reel-like-${media.id}`} className="flex flex-col items-center text-white">
            <span className={`w-12 h-12 flex items-center justify-center border ${liked ? "bg-[#E60000] border-[#E60000]" : "border-white/30 bg-black/40 hover:bg-white/10"}`}>
              <Heart className="w-5 h-5" fill={liked ? "white" : "none"} strokeWidth={1.5} />
            </span>
            <span className="text-xs font-mono mt-1">{likes}</span>
          </button>
          <button data-testid={`reel-comment-${media.id}`} className="flex flex-col items-center text-white">
            <span className="w-12 h-12 flex items-center justify-center border border-white/30 bg-black/40 hover:bg-white/10">
              <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
            </span>
            <span className="text-xs font-mono mt-1">—</span>
          </button>
          <button
            onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); }}
            data-testid={`reel-share-${media.id}`}
            className="flex flex-col items-center text-white"
          >
            <span className="w-12 h-12 flex items-center justify-center border border-white/30 bg-black/40 hover:bg-white/10">
              <Share2 className="w-5 h-5" strokeWidth={1.5} />
            </span>
            <span className="text-xs font-mono mt-1">share</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Reels() {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [muted, setMuted] = useState(true);
  const [active, setActive] = useState(0);
  const containerRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/media/reels");
      setReels(res.data || []);
    } catch { toast.error("Failed to load reels"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollTop / window.innerHeight);
    if (idx !== active) setActive(idx);
  };

  return (
    <div className="min-h-screen bg-black text-white grain" data-testid="reels-page">
      <Navbar onSearchOpen={() => setSearchOpen(true)} onUploadOpen={() => setUploadOpen(true)} dark />

      {loading ? (
        <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
          <div className="text-white/50 text-sm flex items-center gap-2">
            <span className="w-2 h-2 bg-[#E60000] animate-pulse" /> Loading the squad feed…
          </div>
        </div>
      ) : reels.length === 0 ? (
        <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
          <div className="text-center" data-testid="reels-empty">
            <Film className="w-12 h-12 mx-auto text-white/30" strokeWidth={1.2} />
            <h2 className="mt-4 font-display text-4xl font-black tracking-tighter">No reels yet.</h2>
            <p className="mt-2 text-white/50">Toggle "Publish as Reel" when you upload a vertical clip.</p>
            <Button className="mt-6 rounded-sm bg-[#E60000] hover:bg-[#CC0000]" onClick={() => setUploadOpen(true)} data-testid="reels-empty-upload">
              <Plus className="w-4 h-4 mr-1" /> Upload a reel
            </Button>
          </div>
        </div>
      ) : (
        <div
          ref={containerRef}
          onScroll={onScroll}
          className="h-[calc(100vh-4rem)] overflow-y-auto scroll-snap-y no-scrollbar"
          data-testid="reels-scroll"
        >
          {reels.map((r, i) => (
            <Reel
              key={r.id}
              media={r}
              active={i === active}
              muted={muted}
              onToggleMute={() => setMuted((m) => !m)}
              onLike={load}
            />
          ))}
        </div>
      )}

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={load} />
      <AISearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
